/* oxlint-disable typescript/consistent-type-assertions -- lint to the future batch ignore */
import { useEmberService } from '@qonto/react-migration-toolkit/react/hooks';
import { useEmberIntl } from '@qonto/react-migration-toolkit/react/hooks/providers';
import { type PolymorphicSegment, PolymorphicSegmentProvider } from '@repo/poly-hooks/segment';
import { type Session, SessionProvider } from '@repo/poly-hooks/session';
import { ConfigProvider } from '@repo/shared-kit/config/config-provider';
import { getEnvironmentVariables } from '@repo/shared-config/app/helper/environment-variables';
import { SentryProvider } from '@repo/runtime-kit/sentry/context';
import { createMessagesLoader, I18nProvider } from '@repo/runtime-kit/i18n/i18n-provider';
import { ZendeskLocalizationProvider } from '@repo/poly-hooks/zendesk-localization';
import { QueryClientProvider } from '@tanstack/react-query';
import { type PropsWithChildren, type ReactNode, Suspense, useCallback, useMemo } from 'react';
import type { IntlConfig } from 'react-intl';
import {
  DEFAULT_LOCALE,
  isLocaleCode,
  type LocaleCode,
} from '@repo/shared-config/constants/locales';
import { NavigateProvider } from '@repo/poly-hooks/routing';
import { useEmberNavigate } from '@repo/poly-hooks/routing/-ember/get-navigate';
import { FlagsProvider } from '@repo/poly-hooks/flags';
import { createEmberSentryAdapter } from 'register-partners-js/utils/sentry-adapter';
import { createEmberZendeskLocalizationAdapter } from 'register-partners-js/utils/zendesk-localization-adapter';
import { fromEmberConfig } from 'register-partners-js/react/config/adapter';

const messagesLoader = createMessagesLoader(async locale => {
  const messages = (await import(`/translations/${locale}.json`)) as {
    default: NonNullable<IntlConfig['messages']>;
  };
  return messages.default;
});

export function CustomProviders({ children }: PropsWithChildren): ReactNode {
  const intl = useEmberIntl();
  const segment = useEmberService('segment');
  const appConfig = useMemo(() => fromEmberConfig(getEnvironmentVariables()), []);
  const navigate = useEmberNavigate();
  const launchdarkly = useEmberService('launchdarkly');
  const session = useEmberService('session');
  const sessionAdapter: Session = session;
  const queryClientManager = useEmberService('queryClientManager');
  const sentry = useEmberService('sentry');
  const sentryAdapter = useMemo(() => createEmberSentryAdapter(sentry), [sentry]);
  const zendeskLocalizationService = useEmberService('zendesk-localization');
  const localeManager = useEmberService('locale-manager');
  const currentUser = useEmberService('currentUser');
  const zendeskLocalization = useMemo(
    () => createEmberZendeskLocalizationAdapter(zendeskLocalizationService),
    [zendeskLocalizationService]
  );
  // Stable identity: I18nProvider's setLocale depends on it, and kit effects depend on setLocale.
  const handleLocaleChange = useCallback(
    (locale: LocaleCode) => {
      void (async () => {
        await localeManager.loadAndSetLocale(locale);
        if (currentUser.user) {
          currentUser.user.set('locale', locale);
          await currentUser.user.save();
        } else {
          sessionStorage.setItem('logged-out-locale-code', locale);
        }
      })();
    },
    [currentUser, localeManager]
  );

  return (
    <ConfigProvider config={appConfig}>
      <SentryProvider sentry={sentryAdapter}>
        <SessionProvider session={sessionAdapter}>
          <NavigateProvider navigate={navigate}>
            <PolymorphicSegmentProvider segment={segment as unknown as PolymorphicSegment}>
              <FlagsProvider flags={launchdarkly.allFlags}>
                <ZendeskLocalizationProvider zendeskLocalization={zendeskLocalization}>
                  <Suspense fallback={null}>
                    <I18nProvider
                      defaultLocale={isLocaleCode(intl.locale) ? intl.locale : DEFAULT_LOCALE}
                      loader={messagesLoader}
                      onLocaleChange={handleLocaleChange}
                    >
                      <QueryClientProvider client={queryClientManager.queryClient}>
                        {children}
                      </QueryClientProvider>
                    </I18nProvider>
                  </Suspense>
                </ZendeskLocalizationProvider>
              </FlagsProvider>
            </PolymorphicSegmentProvider>
          </NavigateProvider>
        </SessionProvider>
      </SentryProvider>
    </ConfigProvider>
  );
}
